/**
 * Quotes query builder — counterpart to `buildBookingsQuery`.
 *
 * Same "one builder, three consumers" contract: the table, the KPI counts and
 * the CSV export all go through here.
 *
 * Differences from bookings, all driven by the schema rather than preference:
 *   - utm_* columns live DIRECTLY on booking_quotes, so attribution needs no
 *     join and none of the !inner hazard applies.
 *   - There is no `is_test` column, so no test-exclusion predicate exists.
 *   - There is no balance/amount_paid, so no balance-due filter.
 *   - service_type permits only point_to_point | hourly (bookings also allow
 *     airport and event).
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@limo/database'
import type { QuoteFilters, QuoteSortField, TableSort } from '@limo/shared'
import { buildIlikeOrFilter } from '@limo/shared'
import type { BuildQueryOptions } from './buildBookingsQuery'

const SEARCH_COLUMNS = [
  'customer_name',
  'customer_email',
  'pickup_address',
  'token',
] as const

const SORT_COLUMNS: Record<QuoteSortField, string> = {
  created_at: 'created_at',
  pickup_datetime: 'pickup_datetime',
  total_amount: 'total_amount',
  expires_at: 'expires_at',
  status: 'status',
}

type Client = SupabaseClient<Database>

export function buildQuotesQuery(
  client: Client,
  accountId: string,
  filters: QuoteFilters,
  sort?: TableSort<QuoteSortField>,
  options: BuildQueryOptions = {}
) {
  const { count = false, headOnly = false } = options

  let query = client
    .from('booking_quotes')
    .select('*, vehicles (name)', {
      ...(count || headOnly ? { count: 'exact' as const } : {}),
      ...(headOnly ? { head: true } : {}),
    })
    .eq('account_id', accountId)
    // Soft-deleted quotes (admin_soft_delete_quote) are hidden everywhere
    .is('deleted_at', null)

  const searchFilter = buildIlikeOrFilter(filters.search, SEARCH_COLUMNS)
  if (searchFilter !== null) {
    query = query.or(searchFilter)
  }

  // Half-open [from, to) — see DateRangeFilter's docs.
  if (filters.pickupDate?.from) query = query.gte('pickup_datetime', filters.pickupDate.from)
  if (filters.pickupDate?.to) query = query.lt('pickup_datetime', filters.pickupDate.to)
  if (filters.createdDate?.from) query = query.gte('created_at', filters.createdDate.from)
  if (filters.createdDate?.to) query = query.lt('created_at', filters.createdDate.to)

  if (filters.status?.length) {
    query = query.in('status', [...filters.status])
  }

  if (filters.amountMin !== undefined) query = query.gte('total_amount', filters.amountMin)
  if (filters.amountMax !== undefined) query = query.lte('total_amount', filters.amountMax)

  if (filters.serviceType?.length) {
    query = query.in('service_type', [...filters.serviceType])
  }
  if (filters.vehicleIds?.length) {
    query = query.in('vehicle_id', [...filters.vehicleIds])
  }
  if (filters.passengerMin !== undefined) {
    query = query.gte('passenger_count', filters.passengerMin)
  }
  if (filters.passengerMax !== undefined) {
    query = query.lte('passenger_count', filters.passengerMax)
  }
  if (filters.roundTrip !== undefined) query = query.eq('return_trip', filters.roundTrip)
  if (filters.airportPickup !== undefined) {
    query = query.eq('is_airport_pickup', filters.airportPickup)
  }
  if (filters.airportDropoff !== undefined) {
    query = query.eq('is_airport_dropoff', filters.airportDropoff)
  }

  // Quote-specific flags.
  if (filters.isOperatorPriced !== undefined) {
    query = query.eq('is_operator_priced', filters.isOperatorPriced)
  }
  if (filters.isFlatRate !== undefined) {
    query = query.eq('is_flat_rate', filters.isFlatRate)
  }
  if (filters.hasDiscount === true) {
    // A discount exists when a type was recorded (percent | flat).
    query = query.not('discount_type', 'is', null)
  } else if (filters.hasDiscount === false) {
    query = query.is('discount_type', null)
  }

  // No join required — utm_* are local columns here.
  if (filters.utmSource?.length) query = query.in('utm_source', [...filters.utmSource])
  if (filters.utmMedium?.length) query = query.in('utm_medium', [...filters.utmMedium])
  if (filters.utmCampaign?.length) query = query.in('utm_campaign', [...filters.utmCampaign])

  const sortColumn = sort ? SORT_COLUMNS[sort.field] : 'created_at'
  query = query.order(sortColumn, { ascending: sort?.direction === 'asc' })
  if (sortColumn !== 'id') {
    query = query.order('id', { ascending: false })
  }

  return query
}
